'use client';

import type { ReactNode } from 'react';

export interface ToggleSwitchOption<T extends string = string> {
  value: T;
  icon: ReactNode;
}

export interface ToggleSwitchProps<T extends string = string> {
  /** The two options to toggle between */
  options: [ToggleSwitchOption<T>, ToggleSwitchOption<T>];
  /** Currently selected value */
  value: T;
  /** Callback when selection changes */
  onChange: (value: T) => void;
  /** Additional class names for the container */
  className?: string;
  /** Size of the switch */
  size?: 'sm' | 'md' | 'lg';
}

const SIZES = {
  sm: { width: 60, height: 30, circle: 22, padding: 4 },
  md: { width: 76, height: 38, circle: 30, padding: 4 },
  lg: { width: 88, height: 44, circle: 36, padding: 4 },
};

/**
 * A pill-shaped toggle switch with two icon options and an animated circular slider.
 *
 * @example
 * ```tsx
 * <ToggleSwitch
 *   options={[
 *     { value: 'grid', icon: <Grid className="w-3.5 h-3.5" /> },
 *     { value: 'list', icon: <List className="w-3.5 h-3.5" /> },
 *   ]}
 *   value={viewMode}
 *   onChange={setViewMode}
 * />
 * ```
 */
export function ToggleSwitch<T extends string = string>({
  options,
  value,
  onChange,
  className = '',
  size = 'md',
}: ToggleSwitchProps<T>) {
  const s = SIZES[size];
  const isSecond = value === options[1].value;

  // Position calculations — the circle and icons must share exact positions
  const firstPos = s.padding;
  const secondPos = s.width - s.circle - s.padding;
  const topPos = (s.height - s.circle) / 2;

  return (
    <button
      type="button"
      onClick={() => onChange(isSecond ? options[0].value : options[1].value)}
      className={`bg-muted/60 border-border/50 relative cursor-pointer rounded-full border ${className}`}
      style={{ width: s.width, height: s.height }}
    >
      {/* Animated circle slider */}
      <div
        className="bg-foreground absolute rounded-full shadow-sm transition-all duration-200 ease-out"
        style={{
          width: s.circle,
          height: s.circle,
          top: topPos,
          left: isSecond ? secondPos : firstPos,
        }}
      />
      {/* First option icon */}
      <div
        className={`absolute z-10 flex items-center justify-center transition-colors duration-200 ${!isSecond ? 'text-background' : 'text-muted-foreground'}`}
        style={{ width: s.circle, height: s.circle, top: topPos, left: firstPos }}
      >
        {options[0].icon}
      </div>
      {/* Second option icon */}
      <div
        className={`absolute z-10 flex items-center justify-center transition-colors duration-200 ${isSecond ? 'text-background' : 'text-muted-foreground'}`}
        style={{ width: s.circle, height: s.circle, top: topPos, left: secondPos }}
      >
        {options[1].icon}
      </div>
    </button>
  );
}

export default ToggleSwitch;
